import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { FiCalendar, FiChevronDown, FiLayers, FiRefreshCw, FiSearch, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import "../../assets/css/dashboard.css";
import {
  AGENTS_BAR_PALETTE,
  DonutChart,
  HorizontalBarChart,
  LineAreaChart,
} from "../../components/dashboard/DashboardCharts";
import AgentService from "../../services/api/agents";
import UserManagementService from "../../services/api/usermanagement";
import { API_RESPONSE, default_size_mcp } from "../../settings/shared/constants";
import {
  fetchAccountTokensUsageSummary,
  fetchDashboardData,
  formatTokenCount,
  getRangeForPreset,
  toISODate,
} from "../../utils/dashboardAnalytics";

const { RangePicker } = DatePicker;

const FILTER_OPTIONS = [
  { key: "7d", label: "7 Days" },
  { key: "14d", label: "14 Days" },
  { key: "30d", label: "30 Days" },
  { key: "custom", label: "Custom Range" },
];

const VIEW_BY_USER_ENABLED = false;

const VIEW_BY_OPTIONS = [
  { key: "account", label: "Account" },
  { key: "project", label: "Project" },
  { key: "user", label: "User", disabled: !VIEW_BY_USER_ENABLED },
];

const PROJECT_DOT_COLORS = [
  "#6366f1", "#0ea5e9", "#8b5cf6", "#10b981", "#f59e0b",
  "#ef4444", "#ec4899", "#06b6d4", "#84cc16", "#f97316",
];

const PROJECT_SELECT_MENU_SEARCH_HEIGHT = 44;

/** Fixed menu position so dropdown is not clipped by toolbar/page overflow. */
const useProjectSelectMenuLayout = (open, anchorRef) => {
  const [layout, setLayout] = useState(null);

  useEffect(() => {
    if (!open || !anchorRef.current) {
      setLayout(null);
      return undefined;
    }

    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - 12;
      const listMaxHeight = Math.max(120, spaceBelow - PROJECT_SELECT_MENU_SEARCH_HEIGHT - 8);

      setLayout({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 200),
        listMaxHeight,
      });
    };

    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, anchorRef]);

  return layout;
};

const DashboardFilterTabs = ({
  options,
  value,
  onChange,
  groupClassName = "",
  role = "radiogroup",
  ariaLabelledby,
  ariaLabel,
}) => {
  const groupRef = useRef(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  const updateIndicator = useCallback(() => {
    const group = groupRef.current;
    if (!group) return;
    const activeBtn = group.querySelector(".dash-filter-btn.active");
    if (!activeBtn) {
      setIndicator({ left: 0, width: 0 });
      return;
    }
    setIndicator({
      left: activeBtn.offsetLeft,
      width: activeBtn.offsetWidth,
    });
  }, []);

  useEffect(() => {
    const run = () => requestAnimationFrame(updateIndicator);
    run();
    const raf2 = requestAnimationFrame(run);
    const group = groupRef.current;
    if (!group) return () => cancelAnimationFrame(raf2);

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(updateIndicator);
      ro.observe(group);
      group.querySelectorAll(".dash-filter-btn").forEach((btn) => ro.observe(btn));
    }

    window.addEventListener("resize", updateIndicator);
    return () => {
      cancelAnimationFrame(raf2);
      ro?.disconnect();
      window.removeEventListener("resize", updateIndicator);
    };
  }, [value, updateIndicator, options]);

  const isTablist = role === "tablist";

  return (
    <div
      className={`dash-filter-group dash-filter-group--animated${groupClassName ? ` ${groupClassName}` : ""}`}
      ref={groupRef}
      role={role}
      aria-labelledby={ariaLabelledby}
      aria-label={ariaLabel}
    >
      <span
        className="dash-filter-indicator"
        style={{
          width: indicator.width,
          transform: `translateX(${indicator.left}px)`,
        }}
        aria-hidden
      />
      {options.map((opt) => (
        <button
          key={opt.key}
          type="button"
          role={isTablist ? "tab" : "radio"}
          aria-checked={!isTablist ? value === opt.key : undefined}
          aria-selected={isTablist ? value === opt.key : undefined}
          disabled={opt.disabled}
          aria-disabled={opt.disabled || undefined}
          className={`dash-filter-btn${value === opt.key ? " active" : ""}`}
          onClick={() => {
            if (!opt.disabled) onChange(opt.key);
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
};

const TokenInlineStat = ({ loading, totalTokens, rangeLabel, badgeLabel, inPanel }) => (
  <div
    className={`dash-token-kpi${loading ? " dash-token-kpi--loading" : ""}${inPanel ? " dash-token-kpi--in-panel" : ""}`}
    role="status"
    aria-live="polite"
    aria-busy={loading}
    aria-label={
      loading
        ? "Loading total tokens"
        : `Total tokens: ${formatTokenCount(totalTokens)}${rangeLabel ? `, ${rangeLabel}` : ""}`
    }
  >
    <span className="dash-token-kpi-icon" aria-hidden>
      <FiLayers size={15} strokeWidth={2.25} />
    </span>
    <div className="dash-token-kpi-main">
      {!inPanel ? <span className="dash-token-kpi-label">Total tokens</span> : null}
      {loading ? (
        <span className="dash-subtitle">Loading...</span>
      ) : (
        <strong className="dash-token-kpi-value">{formatTokenCount(totalTokens)}</strong>
      )}
    </div>
    {!inPanel && badgeLabel ? (
      <span className="dash-token-kpi-range">{badgeLabel}</span>
    ) : null}
  </div>
);

const DashboardProjectSelect = ({
  projects,
  value,
  onChange,
  loading,
  query,
  onQueryChange,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(query || "");
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const menuLayout = useProjectSelectMenuLayout(open, wrapRef);

  useEffect(() => {
    setSearch(query || "");
  }, [query]);

  const enriched = useMemo(
    () =>
      (Array.isArray(projects) ? projects : []).map((p, i) => ({
        id: String(p?.id || p?._id || i),
        createdBy: p?.createdBy,
        name: p?.name || "Untitled Project",
        color: PROJECT_DOT_COLORS[i % PROJECT_DOT_COLORS.length],
      })),
    [projects],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return q
      ? enriched.filter((p) => {
          const name = (p.name || "").toLowerCase();
          const id = (p.id || "").toLowerCase();
          const createdBy = (p.createdBy || "").toLowerCase();
          return name.includes(q) || id.includes(q) || createdBy.includes(q);
        })
      : enriched;
  }, [enriched, search]);

  const selected = enriched.find((p) => p.id === String(value)) || null;

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (
        wrapRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
      setSearch("");
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const handleSelect = (proj) => {
    onChange(proj.id);
    setOpen(false);
    setSearch("");
  };

  const menuNode =
    open && menuLayout
      ? createPortal(
          <div
            ref={menuRef}
            className="dash-project-select-menu dash-project-select-menu--fixed"
            role="listbox"
            style={{
              top: menuLayout.top,
              left: menuLayout.left,
              width: menuLayout.width,
              "--dash-project-select-list-max": `${menuLayout.listMaxHeight}px`,
            }}
          >
            <div className="dash-project-select-search">
              <FiSearch size={13} style={{ color: "#94a3b8", flexShrink: 0 }} aria-hidden />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearch(val);
                  if (typeof onQueryChange === "function") onQueryChange(val);
                }}
                placeholder="Search projects…"
                aria-label="Search projects"
              />
              {search ? (
                <FiX
                  size={12}
                  style={{ color: "#94a3b8", cursor: "pointer", flexShrink: 0 }}
                  onClick={() => {
                    setSearch("");
                    if (typeof onQueryChange === "function") onQueryChange("");
                  }}
                  aria-label="Clear search"
                />
              ) : null}
            </div>
            <div className="dash-project-select-list">
              {filtered.length === 0 ? (
                <div className="dash-project-select-empty">No projects found</div>
              ) : (
                filtered.map((proj) => (
                  <button
                    key={proj.id}
                    type="button"
                    role="option"
                    aria-selected={proj.id === String(value)}
                    className={`dash-project-select-option${proj.id === String(value) ? " active" : ""}`}
                    onClick={() => handleSelect(proj)}
                  >
                    <span
                      className="dash-project-select-dot"
                      style={{ background: proj.color }}
                      aria-hidden
                    />
                    <span className="dash-project-select-option-text">
                      <span className="dash-project-select-option-name">{proj.name}</span>
                      {proj.createdBy ? (
                        <span className="dash-project-select-option-meta">{proj.createdBy}</span>
                      ) : null}
                    </span>
                    {proj.id === String(value) ? (
                      <span className="dash-project-select-option-check" aria-hidden>
                        ✓
                      </span>
                    ) : null}
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={wrapRef}
      className={`dash-project-select${open ? " dash-project-select--open" : ""}`}
    >
      <button
        type="button"
        className="dash-project-select-trigger"
        disabled={loading}
        onClick={() => !loading && setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {loading ? (
          <span className="dash-project-select-label dash-project-select-label--placeholder">
            Loading projects…
          </span>
        ) : selected ? (
          <>
            <span
              className="dash-project-select-dot"
              style={{ background: selected.color }}
              aria-hidden
            />
            <span className="dash-project-select-label">{selected.name}</span>
          </>
        ) : (
          <span className="dash-project-select-label dash-project-select-label--placeholder">
            Select a project…
          </span>
        )}
        <FiChevronDown size={14} className="dash-project-select-chevron" aria-hidden />
      </button>
      {menuNode}
    </div>
  );
};

const formatUserName = (user) => {
  const fullName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
  return fullName || user?.email || user?.userName || "Unknown user";
};

const DashboardUserSelect = ({ users, value, onChange, loading }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const menuLayout = useProjectSelectMenuLayout(open, wrapRef);

  const enriched = useMemo(
    () =>
      (Array.isArray(users) ? users : []).map((u, i) => ({
        id: String(u?.id || u?.userId || u?.email || i),
        name: formatUserName(u),
        email: u?.email || "",
        color: PROJECT_DOT_COLORS[i % PROJECT_DOT_COLORS.length],
      })),
    [users],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return q
      ? enriched.filter((u) => {
          const name = (u.name || "").toLowerCase();
          const email = (u.email || "").toLowerCase();
          const id = (u.id || "").toLowerCase();
          return name.includes(q) || email.includes(q) || id.includes(q);
        })
      : enriched;
  }, [enriched, search]);

  const selected = enriched.find((u) => u.id === String(value)) || null;

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (
        wrapRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
      setSearch("");
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const handleSelect = (user) => {
    onChange(user.id);
    setOpen(false);
    setSearch("");
  };

  const menuNode =
    open && menuLayout
      ? createPortal(
          <div
            ref={menuRef}
            className="dash-project-select-menu dash-project-select-menu--fixed"
            role="listbox"
            style={{
              top: menuLayout.top,
              left: menuLayout.left,
              width: menuLayout.width,
              "--dash-project-select-list-max": `${menuLayout.listMaxHeight}px`,
            }}
          >
            <div className="dash-project-select-search">
              <FiSearch size={13} style={{ color: "#94a3b8", flexShrink: 0 }} aria-hidden />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users…"
                aria-label="Search users"
              />
              {search ? (
                <FiX
                  size={12}
                  style={{ color: "#94a3b8", cursor: "pointer", flexShrink: 0 }}
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                />
              ) : null}
            </div>
            <div className="dash-project-select-list">
              {filtered.length === 0 ? (
                <div className="dash-project-select-empty">No users found</div>
              ) : (
                filtered.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    role="option"
                    aria-selected={user.id === String(value)}
                    className={`dash-project-select-option${user.id === String(value) ? " active" : ""}`}
                    onClick={() => handleSelect(user)}
                  >
                    <span
                      className="dash-project-select-dot"
                      style={{ background: user.color }}
                      aria-hidden
                    />
                    <span className="dash-project-select-option-text">
                      <span className="dash-project-select-option-name">{user.name}</span>
                      {user.email ? (
                        <span className="dash-project-select-option-meta">{user.email}</span>
                      ) : null}
                    </span>
                    {user.id === String(value) ? (
                      <span className="dash-project-select-option-check" aria-hidden>
                        ✓
                      </span>
                    ) : null}
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={wrapRef}
      className={`dash-project-select dash-scope-user-select${open ? " dash-project-select--open" : ""}`}
    >
      <button
        type="button"
        className="dash-project-select-trigger"
        disabled={loading}
        onClick={() => !loading && setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {loading ? (
          <span className="dash-project-select-label dash-project-select-label--placeholder">
            Loading users…
          </span>
        ) : selected ? (
          <>
            <span
              className="dash-project-select-dot"
              style={{ background: selected.color }}
              aria-hidden
            />
            <span className="dash-project-select-label">{selected.name}</span>
          </>
        ) : (
          <span className="dash-project-select-label dash-project-select-label--placeholder">
            Select a user…
          </span>
        )}
        <FiChevronDown size={14} className="dash-project-select-chevron" aria-hidden />
      </button>
      {menuNode}
    </div>
  );
};

const ChartBlock = ({ title, subtitle, children, variant }) => (
  <div className={`dash-chart-flat${variant === "soft" ? " dash-chart-flat--soft" : ""}`}>
    <div className="dash-chart-head">
      <h3 className="dash-chart-title">{title}</h3>
      {subtitle ? <p className="dash-chart-sub">{subtitle}</p> : null}
    </div>
    <div className="dash-chart-body">{children}</div>
  </div>
);

export default function DashboardPage() {
  const navigate = useNavigate();
  const defaultRange = useMemo(() => getRangeForPreset("7d"), []);
  const [rangePreset, setRangePreset] = useState("7d");
  const [customRange, setCustomRange] = useState([
    dayjs(defaultRange.startDate),
    dayjs(defaultRange.endDate),
  ]);
  const [loading, setLoading] = useState(true);
  const [tokensLoading, setTokensLoading] = useState(true);
  const [accountTotalTokens, setAccountTotalTokens] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [analyticsScope, setAnalyticsScope] = useState("account");
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [projectQuery, setProjectQuery] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");

  const selectedProject = useMemo(() => {
    const id = String(selectedProjectId || "");
    if (!id) return null;
    return (Array.isArray(projects) ? projects : []).find(
      (p) => String(p?.id || p?._id || "") === id,
    ) || null;
  }, [projects, selectedProjectId]);

  const selectedUser = useMemo(() => {
    const id = String(selectedUserId || "");
    if (!id) return null;
    return (Array.isArray(users) ? users : []).find(
      (u) => String(u?.id || u?.userId || u?.email || "") === id,
    ) || null;
  }, [users, selectedUserId]);

  const activeProjectId = analyticsScope === "project" ? selectedProjectId || null : null;
  const activeUserId =
    VIEW_BY_USER_ENABLED && analyticsScope === "user" ? selectedUserId || null : null;

  const { startDate, endDate } = useMemo(() => {
    if (rangePreset === "custom" && customRange?.[0] && customRange?.[1]) {
      return {
        startDate: toISODate(customRange[0].toDate()),
        endDate: toISODate(customRange[1].toDate()),
      };
    }
    return getRangeForPreset(rangePreset);
  }, [rangePreset, customRange]);

  useEffect(() => {
    if (analyticsScope !== "project") return undefined;

    let cancelled = false;
    const q = projectQuery.trim();
    const payload = q.length >= 2 ? { name: q } : {};

    const timer = setTimeout(() => {
      setProjectsLoading(true);
      AgentService.getProject(0, payload)
        .then((res) => {
          if (cancelled) return;
          const list = res?.projectDTOS?.content || [];
          const next = Array.isArray(list) ? list : [];
          setProjects(next);

          const selected = String(selectedProjectId || "");
          const stillExists =
            selected && next.some((p) => String(p?.id || p?._id || "") === selected);

          if (!stillExists) {
            setSelectedProjectId(String(next[0]?.id || next[0]?._id || ""));
          }
        })
        .finally(() => {
          if (!cancelled) setProjectsLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [analyticsScope, projectQuery, selectedProjectId]);

  useEffect(() => {
    if (!VIEW_BY_USER_ENABLED || analyticsScope !== "user") return undefined;

    let cancelled = false;
    const accountId = localStorage.getItem("accountId");
    if (!accountId) {
      setUsers([]);
      setUsersLoading(false);
      return undefined;
    }

    setUsersLoading(true);
    UserManagementService.getuserlist(accountId, 0, default_size_mcp)
      .then((res) => {
        if (cancelled) return;
        const list =
          res?.status === API_RESPONSE.SUCCESS && Array.isArray(res.userDTOS)
            ? res.userDTOS
            : [];
        setUsers(list);

        const selected = String(selectedUserId || "");
        const stillExists =
          selected &&
          list.some((u) => String(u?.id || u?.userId || u?.email || "") === selected);

        if (!stillExists) {
          setSelectedUserId(String(list[0]?.id || list[0]?.userId || list[0]?.email || ""));
        }
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      })
      .finally(() => {
        if (!cancelled) setUsersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [analyticsScope, selectedUserId]);

  const loadAccountTokens = useCallback(async () => {
  setTokensLoading(true);
  try {
    const result = await fetchAccountTokensUsageSummary({
      startDate,
      endDate,
    });
    setAccountTotalTokens(result?.totalTokens ?? 0);
  } catch (e) {
    console.error("[Dashboard] account tokens load failed", e);
    setAccountTotalTokens(0);
  } finally {
    setTokensLoading(false);
  }
}, [activeProjectId, activeUserId]);  // only re-fetches on scope/project change

useEffect(() => {
  loadAccountTokens();
}, [loadAccountTokens]);

  const loadData = useCallback(async () => {
    if (analyticsScope === "project" && !activeProjectId) {
      setData(null);
      setLoading(false);
      return;
    }
    if (VIEW_BY_USER_ENABLED && analyticsScope === "user" && !activeUserId) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const result = await fetchDashboardData({
        startDate,
        endDate,
        rangePreset,
        projectId: activeProjectId,
        userId: activeUserId,
      });
      setData(result);
    } catch (e) {
      console.error("[Dashboard] load failed", e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, rangePreset, analyticsScope, activeProjectId, activeUserId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const daily = data?.dailySeries || [];
  const sf = data?.successFailed || { success: 0, failed: 0 };
  const topAgents = data?.topAgents || [];

  const rangeLabel =
    rangePreset === "custom"
      ? `${startDate} → ${endDate}`
      : FILTER_OPTIONS.find((f) => f.key === rangePreset)?.label || "7 Days";

  const tokenBadgeLabel = "Overall";

  const chartsRowThreeCols = rangePreset === "7d" ? 3 : 2;

  const handleRefreshPage = useCallback(() => {
    setIsRefreshing(true);
    window.location.reload();
  }, []);

  const handleClose = () => navigate("/apps/");

  return (
    <div className="dash-page">
      <div className="dash-inner">
        <header className="dash-header">
          <div className="dash-header-row dash-header-row--title">
            <div className="dash-header-title-block">
              <h1 className="dash-title">Agent Analytics Dashboard</h1>
              <p className="dash-subtitle">
                Monitor tokens, usage, and agent performance across your workspace.
              </p>
            </div>
            <div className="dash-header-actions">
              <button
                type="button"
                className={`dash-refresh-btn${isRefreshing || loading ? " dash-refresh-btn--busy" : ""}`}
                onClick={handleRefreshPage}
                disabled={isRefreshing || loading}
                title="Refresh page"
                aria-label="Refresh page"
              >
                <FiRefreshCw size={15} className="dash-refresh-icon" aria-hidden />
                <span>{isRefreshing ? "Refreshing…" : "Refresh"}</span>
              </button>
              <button
                type="button"
                className="dash-close-btn"
                onClick={handleClose}
                title="Close dashboard"
                aria-label="Close dashboard"
              >
                <FiX size={18} aria-hidden />
              </button>
            </div>
          </div>

          <div className="dash-header-row dash-header-row--toolbar">
            <section
              className="dash-toolbar-panel dash-toolbar-panel--metrics"
              aria-labelledby="dash-metrics-label"
            >
              <div className="dash-toolbar-panel-heading">
                <span id="dash-metrics-label" className="dash-toolbar-panel-label">
                  Total tokens
                </span>
                {tokenBadgeLabel ? (
                  <span className="dash-toolbar-panel-meta">{tokenBadgeLabel}</span>
                ) : null}
              </div>
              <div className="dash-toolbar-panel-controls">
                <TokenInlineStat
                  inPanel
                  loading={tokensLoading}
                  totalTokens={accountTotalTokens}
                  rangeLabel={rangeLabel}
                  badgeLabel={tokenBadgeLabel}
                />
              </div>
            </section>

            <div className="dash-toolbar-divider" aria-hidden />

            <div className="dash-toolbar-filters">
              <section
                className="dash-toolbar-panel dash-toolbar-panel--scope"
                aria-labelledby="dash-view-by-label"
              >
                <div className="dash-toolbar-panel-heading">
                  <span id="dash-view-by-label" className="dash-toolbar-panel-label">
                    View by
                  </span>
                </div>
                <div
                  className={`dash-toolbar-panel-controls dash-toolbar-panel-controls--scope dash-toolbar-panel-controls--scope-${analyticsScope}`}
                >
                  <DashboardFilterTabs
                    options={VIEW_BY_OPTIONS}
                    value={analyticsScope}
                    onChange={setAnalyticsScope}
                    ariaLabelledby="dash-view-by-label"
                  />
                  {VIEW_BY_USER_ENABLED && analyticsScope === "user" && (
                    <div key="scope-user" className="dash-toolbar-scope-extra">
                      <DashboardUserSelect
                        users={users}
                        value={selectedUserId}
                        onChange={setSelectedUserId}
                        loading={usersLoading}
                      />
                    </div>
                  )}
                  {analyticsScope === "project" && (
                    <div key="scope-project" className="dash-toolbar-scope-extra">
                      <DashboardProjectSelect
                        projects={projects}
                        value={selectedProjectId}
                        onChange={setSelectedProjectId}
                        loading={projectsLoading}
                        query={projectQuery}
                        onQueryChange={setProjectQuery}
                      />
                    </div>
                  )}
                </div>
              </section>

              <section
                className="dash-toolbar-panel dash-toolbar-panel--range"
                aria-label="Time range"
              >
                <div className="dash-toolbar-panel-heading">
                  <span className="dash-toolbar-panel-label">Time range</span>
                  <span className="dash-toolbar-panel-meta">{rangeLabel}</span>
                </div>
                <div
                  className={`dash-toolbar-panel-controls dash-toolbar-panel-controls--range dash-toolbar-panel-controls--range-${rangePreset === "custom" ? "custom" : "preset"}`}
                >
                  <DashboardFilterTabs
                    options={FILTER_OPTIONS}
                    value={rangePreset}
                    onChange={setRangePreset}
                    role="tablist"
                    ariaLabel="Time range"
                  />
                  {rangePreset === "custom" && (
                    <div key="range-custom" className="dash-toolbar-scope-extra dash-custom-range dash-custom-range--inline">
                      <FiCalendar size={15} aria-hidden />
                      <RangePicker
                        value={customRange}
                        onChange={(vals) => {
                          if (vals?.[0] && vals?.[1]) setCustomRange(vals);
                        }}
                        allowClear={false}
                        disabledDate={(current) => current && current > dayjs().endOf("day")}
                      />
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>
        </header>

        {loading ? (
          <div className="dash-loading">
            <div className="dash-loading-spinner" />
            <span>Loading analytics…</span>
          </div>
        ) : (
          <>
            <div className="dash-charts-grid dash-charts-grid--pair">
              <ChartBlock title="Total Consumed" subtitle={`Credits per day · ${rangeLabel}`}>
                <LineAreaChart data={daily} valueKey="consumed" color="#3b82f6" />
              </ChartBlock>
              <ChartBlock title="Total Traces" subtitle={`Executions per day · ${rangeLabel}`}>
                <LineAreaChart data={daily} valueKey="traces" color="#0ea5e9" />
              </ChartBlock>
            </div>
            <div
              className={`dash-charts-row-three dash-charts-row-three--cols-${chartsRowThreeCols}`}
            >
              <ChartBlock
                title="Running Time"
                subtitle={`Execution time per day · ${rangeLabel}`}
                variant="soft"
              >
                <LineAreaChart
                  data={daily}
                  valueKey="executionTime"
                  color="#c084fc"
                  soft
                  valueFormat="duration"
                />
              </ChartBlock>
              <ChartBlock
                title="Success vs Failed Requests"
                subtitle="Outcome distribution in selected range"
                variant="soft"
              >
                <DonutChart success={sf.success} failed={sf.failed} />
              </ChartBlock>
              <ChartBlock
                title="Top 5 Agents Usage"
                subtitle="Most active agents by executions"
                variant="soft"
              >
                <HorizontalBarChart items={topAgents} palette={AGENTS_BAR_PALETTE} />
              </ChartBlock>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
